(() => {
  const EVENT_PREFIX = 'wild_yard_';
  const DEBUG_KEY = 'wild_yard_analytics_debug';
  const DEBUG_EVENTS_KEY = 'wild_yard_analytics_events';
  const observedLists = new WeakSet();
  const trackedLists = new Set();
  const googleQueue = [];
  let googleRetryTimer;
  let clarityContextInitialized = false;

  const body = document.body;
  const pageContext = {
    page_type: body?.dataset.wyPageType || 'unknown',
    currency: body?.dataset.wyCurrency || 'USD',
    page_path: window.location.pathname,
  };

  const isDebugging = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('wy_analytics_debug') === '1') return true;
    } catch (_error) {
      return false;
    }

    try {
      return window.localStorage.getItem(DEBUG_KEY) === '1';
    } catch (_error) {
      return false;
    }
  };

  const readDebugEvents = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(DEBUG_EVENTS_KEY) || '[]');
    } catch (_error) {
      return [];
    }
  };

  const renderDebugEvents = (events) => {
    let debugOutput = document.getElementById('WildYardAnalyticsDebug');
    if (!debugOutput) {
      debugOutput = document.createElement('script');
      debugOutput.id = 'WildYardAnalyticsDebug';
      debugOutput.type = 'application/json';
      document.head.appendChild(debugOutput);
    }
    debugOutput.textContent = JSON.stringify(events);
  };

  const compact = (value) => {
    if (Array.isArray(value)) return value.map(compact).filter((item) => item !== undefined);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, item]) => item !== undefined && item !== null && item !== '')
          .map(([key, item]) => [key, compact(item)])
      );
    }
    return value;
  };

  const publishToShopify = (eventName, data) => {
    if (typeof window.Shopify?.analytics?.publish !== 'function') return false;

    try {
      window.Shopify.analytics.publish(eventName, data);
      return true;
    } catch (error) {
      if (isDebugging()) console.warn('[Wild Yard analytics] Shopify publish failed', error);
      return false;
    }
  };

  const publishToClarity = (eventName) => {
    if (typeof window.clarity !== 'function') return false;

    try {
      window.clarity('event', eventName);
      return true;
    } catch (error) {
      if (isDebugging()) console.warn('[Wild Yard analytics] Clarity publish failed', error);
      return false;
    }
  };

  const googlePayload = (data) =>
    compact({
      ...data,
      debug_mode: isDebugging() ? true : undefined,
    });

  const flushGoogleQueue = () => {
    if (typeof window.gtag !== 'function') return false;

    try {
      while (googleQueue.length > 0) {
        const event = googleQueue.shift();
        window.gtag('event', event.name, googlePayload(event.data));
      }
    } catch (error) {
      googleQueue.length = 0;
      if (isDebugging()) console.warn('[Wild Yard analytics] Google queue failed', error);
    }

    if (googleRetryTimer) {
      window.clearInterval(googleRetryTimer);
      googleRetryTimer = undefined;
    }
    return true;
  };

  const publishToGoogle = (eventName, data) => {
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', eventName, googlePayload(data));
        return 'sent';
      } catch (error) {
        if (isDebugging()) console.warn('[Wild Yard analytics] Google publish failed', error);
        return 'failed';
      }
    }

    googleQueue.push({ name: eventName, data });
    if (!googleRetryTimer) {
      let attempts = 0;
      googleRetryTimer = window.setInterval(() => {
        attempts += 1;
        if (flushGoogleQueue() || attempts >= 20) {
          if (attempts >= 20) googleQueue.length = 0;
          window.clearInterval(googleRetryTimer);
          googleRetryTimer = undefined;
        }
      }, 250);
    }
    return 'queued';
  };

  const track = (eventName, data = {}) => {
    try {
      if (typeof eventName !== 'string' || !eventName.startsWith(EVENT_PREFIX)) {
        if (isDebugging()) console.warn(`[Wild Yard analytics] Event names must start with ${EVENT_PREFIX}`);
        return false;
      }

      const payload = compact({
        schema_version: 1,
        ...pageContext,
        ...data,
      });

      const destinations = {
        shopify: publishToShopify(eventName, payload),
        clarity: publishToClarity(eventName),
        google_analytics: publishToGoogle(eventName, payload),
      };

      document.dispatchEvent(
        new CustomEvent('wild-yard:analytics', {
          detail: { event_name: eventName, payload, destinations },
        })
      );

      if (isDebugging()) {
        const debugEvents = readDebugEvents();
        debugEvents.push({ event_name: eventName, payload, destinations });
        window.sessionStorage.setItem(DEBUG_EVENTS_KEY, JSON.stringify(debugEvents.slice(-100)));
        window.__wildYardAnalyticsEvents = debugEvents.slice(-100);
        renderDebugEvents(window.__wildYardAnalyticsEvents);
        console.info('[Wild Yard analytics]', eventName, payload, destinations);
      }

      return true;
    } catch (error) {
      if (isDebugging()) console.warn('[Wild Yard analytics] Event tracking failed', error);
      return false;
    }
  };

  window.WildYardAnalytics = Object.freeze({ track });
  document.documentElement.dataset.wyAnalyticsReady = 'true';
  if (isDebugging()) renderDebugEvents(readDebugEvents());

  const itemFromElement = (element) => {
    if (!(element instanceof HTMLElement)) return null;

    const priceMinor = Number(element.dataset.wyPriceMinor);
    const index = Number(element.dataset.wyItemIndex);

    return compact({
      item_id: element.dataset.wyItemId,
      item_name: element.dataset.wyItemName,
      item_variant_id: element.dataset.wyVariantId,
      item_variant: element.dataset.wyVariantName,
      price: Number.isFinite(priceMinor) ? priceMinor / 100 : undefined,
      currency: element.dataset.wyCurrency || pageContext.currency,
      index: Number.isFinite(index) ? index : undefined,
    });
  };

  const listFromElement = (element) => {
    const list = element.closest('[data-wy-item-list]');

    return compact({
      item_list_id: list?.dataset.wyListId,
      item_list_name: list?.dataset.wyListName,
      item_list_type: list?.dataset.wyListType,
    });
  };

  const observeList = (list) => {
    if (!(list instanceof HTMLElement) || observedLists.has(list)) return;
    observedLists.add(list);

    const key = `${pageContext.page_path}:${list.dataset.wyListId || list.dataset.wyListName || 'list'}`;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || trackedLists.has(key)) return;

        const items = Array.from(list.querySelectorAll('[data-wy-list-item]'))
          .map(itemFromElement)
          .filter(Boolean)
          .slice(0, 24);

        if (items.length === 0) return;

        trackedLists.add(key);
        observer.disconnect();

        track('wild_yard_item_list_view', {
          item_list_id: list.dataset.wyListId,
          item_list_name: list.dataset.wyListName,
          item_list_type: list.dataset.wyListType,
          item_count: items.length,
          items,
        });
      },
      { threshold: 0.08 }
    );

    observer.observe(list);
  };

  const matchingElements = (root, selector) => {
    const elements = [];
    if (root instanceof Element && root.matches(selector)) elements.push(root);
    root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
    return elements;
  };

  const discoverLists = (root = document) => {
    matchingElements(root, '[data-wy-item-list]').forEach(observeList);
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const itemElement = target.closest('[data-wy-list-item]');
      if (itemElement) {
        const interactiveControl = target.closest('button, input, label, select, textarea');
        const productLink = target.closest('a[href*="/products/"]');

        if (productLink || !interactiveControl) {
          track('wild_yard_item_select', {
            ...listFromElement(itemElement),
            ...itemFromElement(itemElement),
            destination_path: productLink ? new URL(productLink.href).pathname : undefined,
          });
        }
      }

      const navigation = target.closest('[data-wy-navigation]');
      if (navigation instanceof HTMLAnchorElement) {
        track('wild_yard_navigation_select', {
          navigation_type: navigation.dataset.wyNavigation,
          navigation_label: navigation.dataset.wyNavigationLabel || navigation.textContent?.trim(),
          destination_path: new URL(navigation.href).pathname,
        });
      }

      const reviewsTrigger = target.closest('[data-wy-reviews-trigger]');
      if (reviewsTrigger instanceof HTMLElement) {
        track('wild_yard_reviews_open', {
          reviews_source: reviewsTrigger.dataset.wyReviewsTrigger,
          // False when Loox has not loaded and the click scrolls to the reviews section instead
          reviews_modal: typeof window.LOOX?.openHCFloater === 'function',
        });
      }

      const searchResult = target.closest(
        '.predictive-search-results__card, .predictive-search-results__card--query'
      );
      const searchLink = searchResult?.querySelector('a[href]') || target.closest('#predictive-search-results a[href]');
      if (searchResult && searchLink instanceof HTMLAnchorElement) {
        const siblings = Array.from(searchResult.parentElement?.children || []);
        track('wild_yard_search_result_select', {
          result_type: searchLink.pathname.includes('/products/') ? 'product' : 'query',
          result_index: siblings.indexOf(searchResult) + 1,
          destination_path: searchLink.pathname,
        });
      }

      const drawerCheckout = target.closest('cart-drawer-component .cart__checkout-button');
      if (drawerCheckout instanceof HTMLButtonElement && !drawerCheckout.disabled) {
        const cartItems = drawerCheckout.closest('cart-items-component');
        const itemCount = Number.parseInt(cartItems?.querySelector('[ref="cartItemCount"]')?.textContent || '', 10);
        track('wild_yard_drawer_checkout_click', {
          // The click waits for a quantity update in flight before it goes to checkout
          cart_update_pending: cartItems?.classList.contains('cart-items-disabled') || false,
          cart_item_count: Number.isNaN(itemCount) ? undefined : itemCount,
        });
      }
    },
    true
  );

  // Bubble phase so `accordion-custom` has had the chance to cancel the toggle.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const summary = target.closest('summary');
    const accordion = summary?.closest('accordion-custom[data-wy-accordion-title]');
    const details = summary?.closest('details');
    if (!summary || !accordion || !details) return;

    const section = accordion.closest('[id^="shopify-section-"]');
    const sectionId = section?.id.replace(/^shopify-section-/, '');

    track('wild_yard_accordion_toggle', {
      accordion_title: accordion.dataset.wyAccordionTitle || summary.textContent?.trim(),
      accordion_id: accordion.dataset.accordionId || accordion.id,
      // The click precedes the browser toggling `open`, so the next state is the inverse.
      accordion_action: details.open ? 'close' : 'open',
      accordion_placement: sectionId?.split('__').pop(),
      item_id: body?.dataset.wyProductId,
      item_name: body?.dataset.wyProductName,
    });
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const option = input.closest('[data-option-name]');
    if (!option) return;

    track('wild_yard_variant_option_select', {
      item_id: body?.dataset.wyProductId,
      item_name: body?.dataset.wyProductName,
      option_name: option?.dataset.optionName,
      option_value: input.value,
      option_available: input.dataset.optionAvailable,
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.field-guide-newsletter__form')) return;

    track('wild_yard_newsletter_submit_attempt', {
      method: 'shopify_customer_form',
      placement: form.dataset.wyPlacement || 'unknown',
    });
  });

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    const type = detail.type;
    const common = compact({
      form_id: detail.formId,
      form_version_id: detail.formVersionId,
      placement: 'klaviyo_form',
    });

    if (type === 'open' || type === 'embedOpen') {
      track('wild_yard_klaviyo_form_open', common);
    } else if (type === 'close') {
      track('wild_yard_klaviyo_form_close', common);
    } else if (type === 'stepSubmit') {
      track('wild_yard_klaviyo_step_submit', {
        ...common,
        step_name: detail.metaData?.$step_name,
      });
    } else if (type === 'submit') {
      track('wild_yard_klaviyo_form_submit', common);
      track('wild_yard_sign_up', {
        method: 'klaviyo_form',
        form_id: detail.formId,
      });
    }
  });

  const initializeDialogTracking = (root = document) => {
    matchingElements(root, 'cart-drawer-component').forEach((drawer) => {
      if (drawer.dataset.wyAnalyticsReady === 'true') return;
      drawer.dataset.wyAnalyticsReady = 'true';
      drawer.addEventListener('dialog:open', () => track('wild_yard_cart_drawer_open'));
    });
  };

  const initializeSuccessTracking = (root = document) => {
    matchingElements(root, '[data-wy-newsletter-success]').forEach((success) => {
      if (success.dataset.wyAnalyticsTracked === 'true') return;
      success.dataset.wyAnalyticsTracked = 'true';
      track('wild_yard_sign_up', {
        method: 'shopify_customer_form',
        placement: success.dataset.wyPlacement || 'unknown',
      });
    });
  };

  const initializeClarityContext = () => {
    if (clarityContextInitialized || typeof window.clarity !== 'function') return;

    try {
      window.clarity('set', 'wild_yard_page_type', pageContext.page_type);
      clarityContextInitialized = true;
    } catch (error) {
      if (isDebugging()) console.warn('[Wild Yard analytics] Clarity context failed', error);
    }
  };

  const initialize = (root = document) => {
    discoverLists(root);
    initializeDialogTracking(root);
    initializeSuccessTracking(root);
  };

  initialize();
  initializeClarityContext();

  // Everything initialize() looks for, so a mutated subtree can be checked with a single query first.
  const trackedSelector = '[data-wy-item-list], cart-drawer-component, [data-wy-newsletter-success]';
  const ignoredMutationTags = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'NOSCRIPT', 'IFRAME']);

  const pendingMutationRoots = new Set();
  let mutationScheduled = false;
  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element && !ignoredMutationTags.has(node.tagName)) pendingMutationRoots.add(node);
      });
    });

    if (pendingMutationRoots.size === 0) return;
    if (mutationScheduled) return;
    mutationScheduled = true;

    window.requestAnimationFrame(() => {
      mutationScheduled = false;
      const roots = Array.from(pendingMutationRoots);
      pendingMutationRoots.clear();
      roots.forEach((root) => {
        // Most added nodes come from third-party widgets (Klaviyo, Loox, shop-js) and contain nothing tracked.
        if (!root.isConnected) return;
        if (!root.matches(trackedSelector) && !root.querySelector(trackedSelector)) return;
        initialize(root);
      });
      initializeClarityContext();
    });
  });

  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
